import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsAlert from '../../../services/NotificationsAlert.jsx';
import Conect from '../../../../Api/Conect.js';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic'];
const MUSIC_EXTENSIONS = ['.mp3', '.wav', '.wma'];
const VIDEO_EXTENSIONS = ['.mkv', '.mp4', '.avi', '.wmv', '.wav'];

const buildFileUrl = (endpoint, path) =>
  `${Conect.defaults.baseURL}/api/${endpoint}?${endpoint === 'stream' ? 'file' : 'path'}=${encodeURIComponent(path)}`;

/**
 * Hook que concentra todo o estado, efeitos e regras de negócio
 * do explorador de arquivos (antigo componente Home).
 */
export function useFileExplorer() {
  const token = localStorage.getItem('token');
  const access = localStorage.getItem('access');
  const nameUser = localStorage.getItem('firstName');
  const lastNameUser = localStorage.getItem('lastName');

  const history = useNavigate();
  const controllerRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [isMusicOpen, setIsMusicOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dados, setDados] = useState(null);
  const [mideaUrl, setMideaUrl] = useState('');
  const [playlist, setPlaylist] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingItem, setEditingItem] = useState(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!token) {
      NotificationsAlert('warning', 'Desconectado', 'Faça login para continuar!');
      history('/login');
    } else {
      getPaths();
    }
    // eslint-disable-next-line
  }, [currentPath, token]);

  const getPaths = async () => {
    try {
      const response = await Conect.post('/api/files', { path: currentPath });
      if (response.data.erro) {
        NotificationsAlert('warning', 'Atenção', response.data.msg);
      } else {
        setDados(response.data.files);
      }
    } catch (error) {
      NotificationsAlert('error', 'Erro', 'Não foi possível carregar os arquivos.');
    }
  };

  const onHome = () => {
    setCurrentPath('');
    getPaths();
  };

  const navigateBack = () => {
    const newPath = currentPath.split('/').slice(0, -1).join('/');
    setCurrentPath(newPath);
  };

  const handleFolderClick = (folderName) => {
    setCurrentPath((prev) => `${prev}/${folderName}`);
  };

  const handleFileDownload = (fileName) => {
    const fileUrl = buildFileUrl('download', `${currentPath}/${fileName}`);
    window.open(fileUrl, '_blank');
  };

  const handlePlayer = (fileName) => {
    setMideaUrl(buildFileUrl('stream', `${currentPath}/${fileName}`));
    setIsPlayerOpen(true);
  };

  const handleMusicPlaylist = (musicInput, coverName) => {
    const musicArray = Array.isArray(musicInput)
      ? musicInput
      : (dados ?? []).filter((file) => file.name === musicInput);

    const tracks = musicArray.map((file) => ({
      name: file.name,
      url: buildFileUrl('stream', `${currentPath}/${file.name}`),
      coverUrl: coverName ? buildFileUrl('stream', `${currentPath}/${coverName}`) : null,
    }));

    setPlaylist(tracks);
    setIsMusicOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewName(item.name);
  };

  const handleRename = async (oldName, updatedName) => {
    if (oldName === updatedName) return;

    try {
      const response = await Conect.post('/api/rename', {
        oldName: `${currentPath}/${oldName}`,
        newName: `${currentPath}/${updatedName}`,
      });
      if (response.data.error) {
        NotificationsAlert('error', 'Erro', response.data.msg);
      } else {
        NotificationsAlert('success', 'Sucesso', 'Arquivo/pasta renomeado com sucesso!');
        getPaths();
      }
    } catch (error) {
      NotificationsAlert('error', 'Erro', 'Falha ao renomear o arquivo/pasta.');
    }
  };

  const handleDelete = async (name) => {
    try {
      const response = await Conect.post('/api/delete', {
        directory: currentPath,
        itemName: name,
      });
      if (response.status === 200) {
        NotificationsAlert('danger', 'Atenção', response.data.msg);
        getPaths();
      } else {
        NotificationsAlert('warning', 'Atenção', response.data.msg);
      }
    } catch (error) {
      NotificationsAlert('error', 'Erro', 'Não foi possível excluir o item.');
    }
  };

  const handleShare = async (item) => {
    try {
      const fullPath = encodeURIComponent(currentPath ? `${currentPath}/${item.name}` : item.name);
      const shareUrl = `${window.location.origin}/ftpserver/share/${fullPath}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = shareUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }

      NotificationsAlert('success', 'Link gerado!', `Link de compartilhamento copiado:\n${shareUrl}`);
    } catch (error) {
      console.error(error);
      NotificationsAlert('error', 'Erro', 'Falha ao gerar link de compartilhamento.');
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('directory', currentPath);
    formData.append('access', access);

    controllerRef.current = new AbortController();

    try {
      const response = await Conect.post('/api/upload', formData, {
        signal: controllerRef.current.signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        },
      });

      if (response.data.error) {
        NotificationsAlert('error', 'Erro', response.data.msg);
      } else {
        NotificationsAlert('success', 'Sucesso', 'Upload realizado!');
        getPaths();
      }
    } catch (error) {
      if (error.name === 'CanceledError') {
        NotificationsAlert('warning', 'Cancelado', 'Upload cancelado pelo usuário.');
      } else {
        NotificationsAlert('error', 'Erro', error?.response?.data?.msg || 'Falha no upload.');
      }
    } finally {
      setUploadProgress(0);
      controllerRef.current = null;
    }
  };

  const openUpload = () => setIsModalOpen(true);

  const cancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  const handleImageView = (fileName) => {
    setSelectedImage(buildFileUrl('download', `${currentPath}/${fileName}`));
    setIsImageOpen(true);
  };

  const handlePdfView = (fileName) => {
    setPdfUrl(buildFileUrl('download', `${currentPath}/${fileName}`));
  };

  const musicFiles = (dados ?? []).filter((file) =>
    MUSIC_EXTENSIONS.includes(file.extension.toLowerCase())
  );

  const findAlbumArt = () =>
    (dados ?? []).find(
      (f) =>
        f.name.toLowerCase().startsWith('albumartsmall') &&
        IMAGE_EXTENSIONS.includes(f.extension.toLowerCase())
    );

  return {
    // dados de sessão
    token,
    access,
    nameUser,
    lastNameUser,
    // utilitário de URL (reusado pelos componentes para montar src de thumbnails)
    buildFileUrl,
    // estado
    isModalOpen,
    setIsModalOpen,
    isPlayerOpen,
    setIsPlayerOpen,
    isMusicOpen,
    setIsMusicOpen,
    isImageOpen,
    setIsImageOpen,
    selectedImage,
    pdfUrl,
    dados,
    mideaUrl,
    setMideaUrl,
    playlist,
    setPlaylist,
    currentPath,
    uploadProgress,
    editingItem,
    setEditingItem,
    newName,
    setNewName,
    musicFiles,
    findAlbumArt,
    // constantes de extensão
    IMAGE_EXTENSIONS,
    MUSIC_EXTENSIONS,
    VIDEO_EXTENSIONS,
    // ações
    getPaths,
    onHome,
    navigateBack,
    handleFolderClick,
    handleFileDownload,
    handlePlayer,
    handleMusicPlaylist,
    handleEdit,
    handleRename,
    handleDelete,
    handleShare,
    handleUpload,
    openUpload,
    cancelUpload,
    handleImageView,
    handlePdfView,
  };
}